/**
 * AddressCard — "Conny cases nearby".
 *
 * Heyflow widget tag:
 *   <div data-conny-widget="address-card"></div>
 *
 * Required Heyflow fields (any of these aliases is accepted; matching is by
 * suffix so `information.address.street` etc. also works):
 *   - Street      → any field labelled "address" + "street" / "Strasse" / "Straße"
 *   - House       → any field labelled "address" + "house" / "Hausnummer"
 *   - ZIP         → any field labelled "address" + "zip" / "plz" / "postal code"
 *   - or a single combined "Full address" field (parsed into street/house/ZIP).
 *
 * The card reads these sessionStorage keys (populated by heyflow-bridge.ts):
 *   STORAGE_KEYS.street, .house, .zip, .address
 *
 * Behaviour:
 *   - With only a ZIP we show area-level cases (`/api/v1/public/funnel-context`
 *     → `zip.cases`).
 *   - With street + house we additionally show house- and street-level cases.
 */
import { useQuery } from "@tanstack/react-query";
import { getFunnelContext } from "../../shared/api";
import { readAddress } from "../../shared/address";
import { AnimatedNumber } from "../../shared/AnimatedNumber";
import { CheckIcon, PinIcon } from "../../shared/icons";
import { useStorage, STORAGE_KEYS } from "../../shared/storage";

function useReactiveAddress() {
  // Subscribe to all 4 keys so the component re-renders when any changes.
  useStorage(STORAGE_KEYS.street);
  useStorage(STORAGE_KEYS.house);
  useStorage(STORAGE_KEYS.zip);
  useStorage(STORAGE_KEYS.address);
  return readAddress();
}

export function AddressCard() {
  const addr = useReactiveAddress();
  const enabled = Boolean(addr.zip || (addr.street && addr.houseNumber));

  const { data } = useQuery({
    queryKey: ["funnel-context", addr.street, addr.houseNumber, addr.zip],
    queryFn: () =>
      getFunnelContext({
        street: addr.street,
        house: addr.houseNumber,
        zip: addr.zip,
      }),
    enabled,
    staleTime: 5 * 60_000,
  });

  if (!enabled) return null;

  const houseCount = data?.address?.house_cases ?? 0;
  const streetCount = data?.address?.street_cases ?? 0;
  const areaCount = data?.zip?.cases ?? 0;

  if (data && houseCount === 0 && streetCount === 0 && areaCount === 0)
    return null;

  return (
    <div className="cn">
      <div className="cn-card cn-fade-in">
        <div className="cn-top">
          <div className="cn-icon">
            <PinIcon />
          </div>
          <div className="cn-head">
            <span>Conny cases nearby</span>
          </div>
        </div>

        <div className="cn-body">
          {houseCount > 0 && <Row label="in your house" count={houseCount} />}
          {streetCount > 0 && (
            <Row label="in your street" count={streetCount} />
          )}
          {areaCount > 0 && (
            <Row
              label={`in your area (ZIP-code: ${addr.zip || "—"})`}
              count={areaCount}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, count }: { label: string; count: number }) {
  if (count <= 0) return null;
  const noun = count === 1 ? "case" : "cases";
  return (
    <div className="cn-row">
      <span className="cn-row-left">
        <span className="cn-check">
          <CheckIcon />
        </span>
        <span className="cn-row-label">{label}</span>
      </span>
      <span className="cn-chip">
        <AnimatedNumber value={count} />
        &nbsp;{noun}
      </span>
    </div>
  );
}
